import { useState, useEffect, useMemo } from "react";
import { api } from "../api";
import Icon from "../components/Icon";
import KbdKey from "../components/KbdKey";
import type { AssetRow } from "../types";
import type { Tab } from "../shell/Subnav";

type CpItem = {
  group: string;
  icon: string;
  title: string;
  meta: string;
  run: () => void;
};

type Props = {
  clientId: string | null;
  clientName: string | null;
  canSwitchClient: boolean;
  onClose: () => void;
  onNavigate: (tab: Tab) => void;
  onOpenAsset: (id: string) => void;
  onNewAsset: () => void;
  onSwitchClient: () => void;
};

export default function CommandPalette({
  clientId, clientName, canSwitchClient, onClose, onNavigate, onOpenAsset, onNewAsset, onSwitchClient,
}: Props) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [assets, setAssets] = useState<AssetRow[]>([]);

  useEffect(() => {
    if (!clientId) return;
    let live = true;
    api.getAssets(clientId).then((d) => { if (live) setAssets(d.assets); }).catch(() => {});
    return () => { live = false; };
  }, [clientId]);

  const items = useMemo<CpItem[]>(() => {
    const all: CpItem[] = [];
    if (clientId) {
      all.push({ group: "Go to", icon: "list", title: "Catalog", meta: clientName ?? "", run: () => onNavigate("catalog") });
      all.push({ group: "Go to", icon: "inbox", title: "Change requests", meta: clientName ?? "", run: () => onNavigate("requests") });
      all.push({ group: "Go to", icon: "history", title: "History", meta: clientName ?? "", run: () => onNavigate("history") });
      all.push({ group: "Go to", icon: "settings", title: "Settings", meta: clientName ?? "", run: () => onNavigate("settings") });
      if (canSwitchClient) all.push({ group: "Go to", icon: "users", title: "Switch client", meta: "See all clients", run: () => onSwitchClient() });
    }
    assets.slice(0, 8).forEach((a) =>
      all.push({ group: "Assets", icon: "file", title: a.title, meta: a.slug ? `/${a.slug}` : "", run: () => onOpenAsset(a.id) })
    );
    all.push({ group: "Actions", icon: "plus", title: "New asset", meta: "", run: () => onNewAsset() });
    return all;
  }, [clientId, clientName, canSwitchClient, assets, onNavigate, onOpenAsset, onNewAsset, onSwitchClient]);

  const filtered = useMemo(() => {
    if (!q) return items;
    const lq = q.toLowerCase();
    return items.filter((i) => i.title.toLowerCase().includes(lq) || i.meta.toLowerCase().includes(lq));
  }, [q, items]);

  const groups = useMemo(() => {
    const m = new Map<string, CpItem[]>();
    filtered.forEach((i) => {
      if (!m.has(i.group)) m.set(i.group, []);
      m.get(i.group)!.push(i);
    });
    return Array.from(m.entries());
  }, [filtered]);

  useEffect(() => { setActive(0); }, [q]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
      if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
      if (e.key === "Enter") {
        e.preventDefault();
        const item = filtered[active];
        if (item) { item.run(); onClose(); }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [filtered, active, onClose]);

  let flatIdx = -1;

  return (
    <div className="cp-scrim" onClick={onClose}>
      <div className="cp" onClick={(e) => e.stopPropagation()}>
        <input
          className="cp-input"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search assets, change requests, settings"
        />
        <div className="cp-list">
          {groups.map(([g, list]) => (
            <div key={g}>
              <div className="cp-group-head">{g}</div>
              {list.map((i) => {
                flatIdx++;
                const idx = flatIdx;
                return (
                  <div
                    key={`${g}-${idx}`}
                    className={`cp-item${idx === active ? " active" : ""}`}
                    onClick={() => { i.run(); onClose(); }}
                    onMouseEnter={() => setActive(idx)}
                  >
                    <Icon name={i.icon} size={15} />
                    <span className="title">{i.title}</span>
                    <span className="meta">{i.meta}</span>
                  </div>
                );
              })}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="cp-item" style={{ color: "var(--fg-muted)" }}>
              <Icon name="search" size={15} />
              <span className="title">No match for "{q}"</span>
            </div>
          )}
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--rule)", display: "flex", justifyContent: "space-between", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg-muted)", letterSpacing: "0.04em" }}>
          <span><KbdKey>↑</KbdKey> <KbdKey>↓</KbdKey> to move · <KbdKey>⏎</KbdKey> to open</span>
          <span><KbdKey>esc</KbdKey> to close</span>
        </div>
      </div>
    </div>
  );
}
